/**
 * One name, hue and colour per order workflow status, for the Board, the List,
 * client detail and the dashboard. Labels and hues follow docs/backoffice-design.md
 * § Status Colors, which the consumer app shares; the colours live in src/index.css
 * as the --kanban-* tokens, one hue per status in both themes.
 *
 * Cancelled isn't a workflow status: a cancel leaves `status` alone, so use
 * isCancelledOrder, which reads the payment status.
 *
 * Courier hand-off is not a kanban column. The board stays on ready_for_pickup
 * (labelled "Awaiting completion") until completed; the courier_orders ladder
 * is shown as a badge. Legacy rows may still carry `courier_assigned` — treat
 * them as ready for that column.
 */

export type OrderWorkflowStatus =
  | 'not_started'
  | 'planned'
  | 'preparation_in_progress'
  | 'ready_for_pickup'
  | 'completed'

/** The status's hue, as a Tailwind colour family. */
export type OrderStatusTone = 'slate' | 'blue' | 'amber' | 'violet' | 'teal' | 'green'

export interface OrderStatusDisplay {
  status: string
  label: string
  tone: OrderStatusTone
  /** CSS variable that holds the status colour as "R G B", e.g. `--kanban-ready`. */
  token: string
}

export interface OrderStatusEntry extends OrderStatusDisplay {
  status: OrderWorkflowStatus
}

/** The workflow in order, which is also the Board's columns from left to right. */
export const ORDER_STATUSES: readonly OrderStatusEntry[] = [
  { status: 'not_started', label: 'Not started', tone: 'slate', token: '--kanban-not-started' },
  { status: 'planned', label: 'Planned', tone: 'blue', token: '--kanban-planned' },
  { status: 'preparation_in_progress', label: 'In preparation', tone: 'amber', token: '--kanban-in-progress' },
  { status: 'ready_for_pickup', label: 'Awaiting completion', tone: 'violet', token: '--kanban-ready' },
  { status: 'completed', label: 'Completed', tone: 'green', token: '--kanban-completed' },
]

const BY_STATUS = new Map<string, OrderStatusEntry>(ORDER_STATUSES.map(entry => [entry.status, entry]))

/** Normalise a raw progress code onto a board column id. */
export function boardColumnStatus(status?: string | null): OrderWorkflowStatus {
  const raw = (status || '').trim().toLowerCase().replace(/[\s_-]+/g, '_')
  if (raw === 'courier_assigned') return 'ready_for_pickup'
  if (BY_STATUS.has(raw)) return raw as OrderWorkflowStatus
  return 'not_started'
}

/**
 * The map's entry for a status, in any case or spacing ("Ready For Pickup").
 * No status means the order hasn't started. Anything else the map doesn't know
 * gets a readable label ("awaiting_quote" → "Awaiting quote") and the grey of
 * Not started.
 */
export function orderStatusDisplay(status?: string | null): OrderStatusDisplay {
  const raw = (status || '').trim()
  if (!raw) return BY_STATUS.get('not_started')!
  const key = raw.toLowerCase().replace(/[\s_-]+/g, '_')
  if (key === 'courier_assigned') return BY_STATUS.get('ready_for_pickup')!
  const known = BY_STATUS.get(key)
  if (known) return known
  const words = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase()
  return {
    status: raw,
    label: words.charAt(0).toUpperCase() + words.slice(1),
    tone: 'slate',
    token: '--kanban-not-started',
  }
}

/** "In preparation" for `preparation_in_progress`; see orderStatusDisplay for the rest. */
export function formatOrderStatusLabel(status?: string | null): string {
  return orderStatusDisplay(status).label
}

/** The status colour for inline styles and SVG: `rgb(var(--kanban-ready))`. */
export function orderStatusColor(status?: string | null): string {
  return `rgb(var(${orderStatusDisplay(status).token}))`
}

/** Courier job ladder — badge only, not a board column. */
export const COURIER_ORDER_STATUSES = [
  'offered',
  'accepted',
  'picked_up',
  'delivering',
  'delivered',
  'cancelled',
] as const

export type CourierOrderStatusCode = (typeof COURIER_ORDER_STATUSES)[number]

const COURIER_LABELS: Record<CourierOrderStatusCode, string> = {
  offered: 'Offered',
  accepted: 'Accepted',
  picked_up: 'Picked up',
  delivering: 'Delivering',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

const COURIER_TONES: Record<CourierOrderStatusCode, OrderStatusTone> = {
  offered: 'amber',
  accepted: 'teal',
  picked_up: 'teal',
  delivering: 'teal',
  delivered: 'green',
  cancelled: 'slate',
}

export function courierOrderStatusDisplay(status?: string | null): OrderStatusDisplay | null {
  const raw = (status || '').trim().toLowerCase().replace(/[\s_-]+/g, '_')
  if (!raw) return null
  if (!(COURIER_ORDER_STATUSES as readonly string[]).includes(raw)) {
    const words = raw.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()
    return {
      status: raw,
      label: words.charAt(0).toUpperCase() + words.slice(1),
      tone: 'teal',
      token: '--kanban-courier',
    }
  }
  const code = raw as CourierOrderStatusCode
  return {
    status: code,
    label: COURIER_LABELS[code],
    tone: COURIER_TONES[code],
    token: '--kanban-courier',
  }
}
